import { isPlatformBrowser } from "@angular/common";
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Inject,
    NgZone,
    PLATFORM_ID,
    ViewChild,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { FieldType, FieldTypeConfig, FormlyFieldConfig } from "@ngx-formly/core";
import SignaturePad from "signature_pad";

@Component({
    standalone: false,
    templateUrl: "./signature-pad.component.html",
})
export class FormlySignaturePad extends FieldType<FieldTypeConfig> implements AfterViewInit {
    @ViewChild("signaturepad") canvas: ElementRef;
    private signaturePad: any;
    isEmptySignatureForRememberOnEdit: boolean = false;
    private domSanitizer: DomSanitizer;
    private ngZone: NgZone;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        domSanitizer: DomSanitizer,
        ngZone: NgZone
    ) {
        super();
        this.domSanitizer = domSanitizer;
        this.ngZone = ngZone;
    }

    @HostListener("window:resize", ["$event"])
    onResize(event) {
        this.canvasResize();
    }

    ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            if (!this.field.props) this.field.props = {};

            // Bind callbacks so props methods keep the component context.
            this.field.props.resize = this.canvasResize.bind(this);
            this.field.props.getSignature = this.getSignature.bind(this);
            this.field.props.clear = this.clear.bind(this);
            this.field.props.isEmpty = this.isEmpty.bind(this);
            this.field.props.cropSignatureCanvas =
                this.cropSignatureCanvas.bind(this);
            this.isEmptySignatureForRememberOnEdit =
                !this.model[this.key as string] && this.props.isRemembered;

            setTimeout(() => {
                this.canvasResize();
            }, 1000);
        }
    }

    canvasResize(field?: FormlyFieldConfig) {
        let thisField: any = field || this;
        let props = thisField.to || thisField.props || {};

        if (
            (!props.disabled || props.isRemembered) &&
            props.signaturePad
        ) {
            let points = props.signaturePad.toDataURL();
            let ratio = Math.max(window.devicePixelRatio || 1, 1);
            let canvas = props.signaturePad.canvas;

            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext("2d").canvas.width = canvas.width;
            if (canvas.height < 500 || canvas.height > 800) canvas.height = 500;
            canvas.getContext("2d").scale(ratio, ratio);
            props.signaturePad.fromDataURL(points);
        }
    }

    ngAfterViewInit() {
        if (this.props.keepSignature && this.model[this.key as string]) {
            this.createSignature(true);
        } else if (
            isPlatformBrowser(this.platformId) &&
            (!this.props.disabled || this.isEmptySignatureForRememberOnEdit)
        ) {
            this.createSignature();
            this.clear();
        }
    }

    private onEnd() {
        if (!this.canvas || !this.canvas.nativeElement) return;

        let signature = this.cropSignatureCanvas(
            this.canvas.nativeElement,
            "image/png"
        );

        if (this.field.props.keepSignature) {
            this.field.props.uncroppedImage =
                this.field.props.signaturePad.toDataURL();
        }

        // Keep both form control and model in sync for downstream entity-based save logic.
        this.field.formControl.setValue(signature);
        this.field.formControl.markAsDirty();
        this.field.formControl.updateValueAndValidity({ emitEvent: true });
        if (this.formControl) {
            this.formControl.setValue(signature);
            this.formControl.markAsDirty();
            this.formControl.updateValueAndValidity({ emitEvent: true });
        }
        this.model[this.key as string] = signature;
    }

    clear() {
        this.signaturePad.clear();
        this.field.formControl.setValue(null);
        if (this.formControl) {
            this.formControl.setValue(null);
        }
        this.model[this.key as string] = null;
    }

    clearRememberedImage() {
        this.props.disabled = false;

        // Drop the remembered value first, otherwise createSignature() below would load it
        // straight back onto the pad the user just asked to clear.
        this.formControl.setValue(null);
        this.model[this.key as string] = null;

        if (this.props.onClear) {
            this.props.onClear();
        }

        setTimeout(() => {
            this.createSignature();
        }, 300);
    }

    getSignature(format: string = "image/png"): string {
        return this.cropSignatureCanvas(this.signaturePad.canvas, format);
    }

    isEmpty(): boolean {
        return this.signaturePad === undefined || this.signaturePad.isEmpty();
    }

    cropSignatureCanvas(canvas: any, format: string) {
        const croppedCanvas = document.createElement("canvas");
        const croppedCtx = croppedCanvas.getContext("2d");

        croppedCanvas.width = canvas.width;
        croppedCanvas.height = canvas.height;
        croppedCtx.drawImage(canvas, 0, 0);

        let w = croppedCanvas.width;
        let h = croppedCanvas.height;
        const pix = { x: [], y: [] };
        const imageData = croppedCtx.getImageData(
            0,
            0,
            croppedCanvas.width,
            croppedCanvas.height
        );
        let x: number;
        let y: number;
        let index: number;

        for (y = 0; y < h; y++) {
            for (x = 0; x < w; x++) {
                index = (y * w + x) * 4;
                if (imageData.data[index] !== 255) {
                    const croppedX = x - 5 >= 0 ? x - 5 : 0;
                    const croppedY = y - 5 >= 0 ? y - 5 : 0;
                    pix.x.push(croppedX);
                    pix.y.push(croppedY);
                }
            }
        }

        if (pix.x.length === 0) {
            return canvas.toDataURL(format);
        }

        pix.x.sort((a, b) => a - b);
        pix.y.sort((a, b) => a - b);
        const n = pix.x.length - 1;

        w = pix.x[n] - pix.x[0] + 10;
        h = pix.y[n] - pix.y[0] + 10;
        const cut = croppedCtx.getImageData(pix.x[0], pix.y[0], w, h);

        croppedCanvas.width = w;
        croppedCanvas.height = h;
        croppedCtx.putImageData(cut, 0, 0);

        return croppedCanvas.toDataURL(format);
    }

    // A saved signature is cropped from a canvas backed at devicePixelRatio device pixels per CSS
    // pixel (see canvasResize), so its image is ratio times larger than it was drawn. Shown at
    // natural size it looks enlarged compared to the signing pad.
    fitViewSignatureToScreenScale(image: HTMLImageElement) {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        image.style.height = `${image.naturalHeight / ratio}px`;
    }

    private loadSavedSignature(dataUrl: string) {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const image = new Image();

        // fromDataURL stretches the image to the whole canvas unless given a size, which blew a
        // cropped signature up to fill the pad - and re-cropping that on the next stroke saved it
        // larger on every edit. Draw it back at the size it was signed at instead.
        image.onload = () => {
            this.signaturePad.fromDataURL(dataUrl, {
                ratio,
                width: image.naturalWidth / ratio,
                height: image.naturalHeight / ratio,
            });
        };

        image.src = dataUrl;
    }

    getSource(path: string) {
        path = path || "";

        if (path.startsWith("data:image/"))
            return this.domSanitizer.bypassSecurityTrustUrl(path);

        return path;
    }

    private createSignature(keepSignature: boolean = false) {
        if (typeof this.canvas != 'undefined') {
            this.field.props.canvas = this.canvas;
            this.signaturePad = new SignaturePad(this.canvas.nativeElement, {
                // It's Necessary to use an opaque color when saving image as JPEG;
                // this option can be omitted if only saving as PNG or SVG
                backgroundColor: "#ffffff",
            });
            this.signaturePad.backgroundColor = "#ffffff";
            this.signaturePad.minWidth = 0.5;
            this.signaturePad.maxWidth = 1.5;
            // Newer signature_pad versions emit endStroke events instead of relying on onEnd callback.
            if (typeof this.signaturePad.addEventListener === "function") {
                this.signaturePad.addEventListener("endStroke", () => {
                    this.ngZone.run(() => this.onEnd());
                });
            }
            this.signaturePad.onEnd = this.onEnd.bind(this);
            this.signaturePad.field = this.field;

            this.field.props.signaturePad = this.signaturePad;

            this.canvasResize();

            if (keepSignature && this.field.props.uncroppedImage) {
                this.signaturePad.fromDataURL(
                    this.field.props.uncroppedImage
                );
            } else if (
                this.props.resetSignature === false &&
                this.model[this.key as string]
            ) {
                // Only a "Remembered on Edit" signature (resetSignature = !isRemembered) carries into
                // edit. Any other signature falls through to the clear() below, so the entry has to
                // be signed again.
                //
                // Must go through signaturePad.fromDataURL(), not a raw context.drawImage(): a
                // directly-painted image is invisible to the library's toDataURL(), so the
                // canvasResize() round-trip ngOnInit schedules would wipe it about a second later.
                this.loadSavedSignature(this.model[this.key as string]);
            } else {
                setTimeout(() => {
                    this.clear();

                    if (this.props.onClear) this.props.onClear();
                }, 700);
            }
        }
    }
}
